import { useEffect, useState } from "react";
import { useCreate } from "@refinedev/core";
import { Button, Form, Input, InputNumber, Modal, Select, Upload } from "antd";
import { UploadFile } from "antd/es/upload/interface";
import { UploadOutlined } from "@ant-design/icons";
import { v4 as randomId } from "uuid";
import { EFileTypes } from "components";
import { leagueOptions } from "pages/Video/leagueOptions";
import {
  MedicineContestRegulation,
  MedicineContestRegulationFormValues,
} from "./types";

type RegulationModalProps = {
  open: boolean;
  contestId: number;
  saving: boolean;
  regulation?: MedicineContestRegulation;
  onCancel: () => void;
  onSubmit: (values: MedicineContestRegulationFormValues) => void;
};

export const RegulationModal = ({
  open,
  contestId,
  saving,
  regulation,
  onCancel,
  onSubmit,
}: RegulationModalProps) => {
  const [form] = Form.useForm<MedicineContestRegulationFormValues>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { mutate: uploadFile, isLoading: uploading } = useCreate();

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue({
      id: regulation?.id,
      contestId,
      leagueId: regulation?.leagueId,
      title: regulation?.title || "",
      fileUrl: regulation?.fileUrl || "",
      ord: regulation?.ord,
    });
    setFileList(
      regulation?.fileUrl
        ? [
            {
              uid: String(regulation.id),
              name: regulation.fileUrl.replace(/.*\//, ""),
              url: regulation.fileUrl,
            },
          ]
        : []
    );
  }, [contestId, form, open, regulation]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onSubmit(values);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal
      title={regulation ? "Редактировать документ" : "Добавить документ"}
      open={open}
      okText="Сохранить"
      cancelText="Отмена"
      confirmLoading={saving}
      onOk={handleSubmit}
      onCancel={onCancel}
      destroyOnClose
    >
      <Form<MedicineContestRegulationFormValues> form={form} layout="vertical">
        <Form.Item name="id" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="contestId" hidden>
          <Input />
        </Form.Item>
        <Form.Item
          label="Файл"
          required
          validateStatus={form.getFieldError("fileUrl").length ? "error" : ""}
          help={form.getFieldError("fileUrl")[0]}
        >
          <Form.Item
            name="fileUrl"
            noStyle
            rules={[{ required: true, message: "Загрузите файл" }]}
          >
            <Input type="hidden" />
          </Form.Item>

          <Upload
            accept=".pdf"
            maxCount={1}
            fileList={fileList}
            customRequest={(request) => {
              uploadFile(
                {
                  dataProviderName: "uploadDataProvider",
                  resource: "",
                  values: {
                    entityId: `medicine/contests/${contestId}/${randomId()}`,
                    serviceType: EFileTypes.DOCUMENTS,
                    blob: request.file as File,
                    type: "DOCUMENTS",
                  },
                },
                {
                  onSuccess: (data: any) => {
                    console.log("UPLOAD RESPONSE:", data);

                    const fileUrl =
                      data?.data?.s3id ||
                      data?.data?.url ||
                      data?.s3id ||
                      data?.url;

                    if (!fileUrl) {
                      request.onError?.(
                        new Error("Не удалось получить ссылку на файл")
                      );
                      return;
                    }

                    form.setFieldsValue({ fileUrl });
                    form.setFields([{ name: "fileUrl", errors: [] }]);

                    setFileList([
                      {
                        uid: (request.file as UploadFile).uid || randomId(),
                        name: (request.file as File).name,
                        status: "done",
                        url: fileUrl,
                      },
                    ]);

                    request.onSuccess?.(data);
                  },
                  onError: (error: any) => {
                    request.onError?.(error);
                  },
                }
              );
            }}
            onRemove={() => {
              setFileList([]);
              form.setFieldValue("fileUrl", undefined);
              return true;
            }}
          >
            <Button
              disabled={!!fileList.length}
              loading={uploading}
              icon={<UploadOutlined />}
            >
              Загрузить PDF
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item
          name="title"
          label="Название"
          rules={[
            {
              required: true,
              message: "Введите название",
            },
          ]}
        >
          <Input placeholder="Положение о конкурсе" />
        </Form.Item>

        <Form.Item
          name="leagueId"
          label="Лига"
          rules={[
            {
              required: true,
              message: "Выберите лигу",
            },
          ]}
        >
          <Select options={leagueOptions} placeholder="Выберите лигу" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
